import React, { useEffect, useState } from "react";
import { 
  CircularProgress, 
  Typography, 
  Box, 
  Fade, 
  useTheme,
  useMediaQuery,
  LinearProgress,
  Zoom 
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import Logo from "../../assets/image/logo.png";

// Animations
const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const pulse = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
  100% {
    transform: scale(1);
  }
`;

const rotate = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

// Styled Components
const StyledContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100vw',
  background: 'linear-gradient(135deg, #1dc39f 0%, #14a085 50%, #0f8b6c 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, transparent 70%)',
    pointerEvents: 'none',
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  animation: `${pulse} 2s ease-in-out infinite`,
  marginBottom: theme.spacing(4),
  '& img': {
    filter: 'drop-shadow(0 8px 16px rgba(0,0,0,0.2))',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.15)',
    padding: theme.spacing(2),
    border: '2px solid rgba(255,255,255,0.3)',
  }
}));

const TitleContainer = styled(Box)(({ theme }) => ({
  animation: `${fadeInUp} 1s ease-out`,
  textAlign: 'center',
  marginBottom: theme.spacing(4),
}));

const LoaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(2),
}));

const StyledCircularProgress = styled(CircularProgress)(({ theme }) => ({
  animation: `${rotate} 2s linear infinite`,
  '& .MuiCircularProgress-circle': {
    strokeLinecap: 'round',
  }
}));

const SplashScreen = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const timer = setInterval(() => {
      setProgress((prevProgress) => (prevProgress >= 100 ? 0 : prevProgress + 10));
    }, 300);

    return () => {
      clearInterval(timer);
    };
  }, []);

  return (
    <StyledContainer>
      <Fade in timeout={800}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            px: { xs: 3, sm: 6 },
            py: { xs: 4, sm: 6 },
            background: 'rgba(255, 255, 255, 0.15)',
            backdropFilter: 'blur(15px)',
            borderRadius: '24px',
            border: '1px solid rgba(255, 255, 255, 0.25)',
            boxShadow: '0 8px 32px rgba(29, 195, 159, 0.2), 0 4px 16px rgba(0,0,0,0.1)',
            maxWidth: { xs: '90vw', sm: '500px' },
            width: '100%',
          }}
        >
          <Zoom in timeout={1000}>
            <LogoContainer>
              <img 
                src={Logo} 
                alt="Logo Pemerintah Kota Makassar" 
                style={{
                  height: isMobile ? '100px' : '150px',
                  width: 'auto',
                }}
              />
            </LogoContainer>
          </Zoom>

          <TitleContainer>
            <Typography 
              variant={isMobile ? "h5" : "h4"}
              fontWeight={700}
              color="white"
              sx={{
                textShadow: '0 2px 4px rgba(0,0,0,0.3)',
                mb: 1,
                background: 'linear-gradient(45deg, #fff 30%, #8ff9ab 70%, #4dd4b8 90%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Form Pengecekan CCTV
            </Typography>
            <Typography 
              variant="body1"
              color="rgba(255,255,255,0.8)"
              sx={{
                textShadow: '0 1px 2px rgba(0,0,0,0.3)',
                fontSize: { xs: '0.875rem', sm: '1rem' }
              }}
            >
              Pemerintah Kota Makassar
            </Typography>
          </TitleContainer>

          <LoaderContainer>
            <Box sx={{ position: 'relative', display: 'inline-flex', mb: 2 }}>
              <StyledCircularProgress
                variant="determinate"
                value={progress}
                size={isMobile ? 60 : 80}
                thickness={4}
                sx={{
                  color: '#8ff9ab',
                  '& .MuiCircularProgress-circle': {
                    strokeLinecap: 'round',
                  }
                }}
              />
              <Box
                sx={{
                  top: 0,
                  left: 0,
                  bottom: 0,
                  right: 0,
                  position: 'absolute',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Typography 
                  variant="caption" 
                  component="div" 
                  color="white"
                  fontWeight={600}
                >
                  {`${Math.round(progress)}%`}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ width: '100%', maxWidth: 200 }}>
              <LinearProgress 
                variant="determinate" 
                value={progress}
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: 'rgba(255,255,255,0.2)',
                  '& .MuiLinearProgress-bar': {
                    backgroundColor: '#8ff9ab',
                    borderRadius: 3,
                    boxShadow: '0 0 10px rgba(143, 249, 171, 0.5)',
                  }
                }}
              />
            </Box>
            
            <Typography 
              variant="body2" 
              color="rgba(255,255,255,0.7)"
              sx={{ 
                mt: 2,
                animation: `${fadeInUp} 2s ease-out`,
                fontSize: { xs: '0.75rem', sm: '0.875rem' }
              }}
            >
              Memuat aplikasi...
            </Typography>
          </LoaderContainer>
        </Box>
      </Fade>
    </StyledContainer>
  );
};

export default SplashScreen;
